import { useState } from 'react'
import { Loader2, UserPlus, X } from 'lucide-react'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { EMPTY_USER_FILTERS, listUsers } from '@/features/users/api'
import { createAssignment, listEventAssignments, withdrawAssignment } from '../api'
import {
  ASSIGNMENT_ROLE_ORDER, assignmentMeaning, assignmentRoleLabel, assignmentStatusLabel,
} from '../vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse } from '@/shared/api/types'

/**
 * Who is supposed to be filming.
 *
 * A feed's `broadcasterId` is a fact about the past — whoever claimed the camera.
 * An assignment is a statement about the future, and it can be wrong, which is
 * exactly why it is a separate row. Without it, the first time anybody discovers
 * that eleven of Saturday's fourteen events have nobody holding a phone is when
 * nothing appears on the live board.
 */
export function AssignmentsPanel({
  event, onChanged,
}: { event: CoverageEventResponse; onChanged: () => void }) {
  const canAssign = useCan('coverage:assign')
  const [tick, setTick] = useState(0)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, error: loadError, loading } = useLoad(
    () => listEventAssignments(event.id), [event.id, tick],
  )
  const rows = (data ?? []).filter((a) => a.statusCode !== 'WITHDRAWN')
  const withdrawn = (data ?? []).filter((a) => a.statusCode === 'WITHDRAWN')
  const over = event.statusCode === 'ENDED' || event.statusCode === 'CANCELLED'
  const hasCamera = rows.some((a) => a.roleCode === 'CAMERA' && a.statusCode !== 'DECLINED')

  return (
    <div className="space-y-3">
      <ErrorStrip message={loadError ?? error} />

      <DrawerSection
        title="Assigned"
        right={canAssign && !over ? (
          <button type="button" className="erp-btn h-7" onClick={() => setAdding(true)}>
            <UserPlus className="h-3.5 w-3.5" /> Assign
          </button>
        ) : undefined}
      >
        {loading && rows.length === 0 ? <LoadingBlock label="Loading assignments…" />
          : rows.length === 0 ? (
            <Note tone={over ? 'info' : 'bad'}>
              {over
                ? 'Nobody was assigned to this event.'
                : 'Nobody is assigned. This event has no camera booked, and the first sign of that will be an empty live board.'}
            </Note>
          ) : (
            <ul className="space-y-1">
              {rows.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-erp-sm font-medium">
                      {a.userName ?? a.userId}
                      {a.mobilizerName && (
                        <span className="ml-1.5 text-erp-xs font-normal text-muted-foreground">
                          {a.mobilizerName}
                        </span>
                      )}
                    </div>
                    <div className="truncate text-erp-xs text-muted-foreground">
                      {assignmentRoleLabel(a.roleCode)} · {assignmentStatusLabel(a.statusCode)}
                      {a.assignedByName && ` · by ${a.assignedByName}`}
                    </div>
                    {/* What the status actually changes — see `assignmentMeaning`. */}
                    <p className="text-erp-xs text-muted-foreground/80">
                      {assignmentMeaning(a.statusCode)}
                    </p>
                    {a.declinedReason && (
                      <p className="text-erp-xs text-amber-700 dark:text-amber-400">
                        Declined: {a.declinedReason}
                      </p>
                    )}
                    {a.note && <p className="text-erp-xs text-muted-foreground">{a.note}</p>}
                  </div>
                  {canAssign && !over && (
                    <button
                      type="button"
                      className="erp-btn h-7 shrink-0"
                      aria-label={`Withdraw ${a.userName ?? 'assignment'}`}
                      onClick={async () => {
                        setError(null)
                        try { await withdrawAssignment(a.id); setTick((t) => t + 1); onChanged() }
                        catch (e) { setError(explain(e)) }
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

        {!over && rows.length > 0 && (
          <div className="mt-2">
            <Note tone="info">
              Being assigned does not grant permission to broadcast — that is a role
              decision, and somebody with the permission can film whether or not they were
              asked. What an <strong>accepted</strong> assignment does is hold the camera:
              an unclaimed camera on this event is then refused to anybody else.
            </Note>
          </div>
        )}

        {!over && rows.length > 0 && !hasCamera && (
          <div className="mt-2">
            <Note tone="bad">
              Somebody is assigned, but nobody on camera. This event still counts as
              uncovered.
            </Note>
          </div>
        )}
      </DrawerSection>

      {withdrawn.length > 0 && (
        <DrawerSection title={`Withdrawn (${withdrawn.length})`}>
          <ul className="space-y-0.5 text-erp-xs text-muted-foreground">
            {withdrawn.map((a) => (
              <li key={a.id} className="truncate">
                {a.userName ?? a.userId} — {assignmentRoleLabel(a.roleCode)}
              </li>
            ))}
          </ul>
          <div className="mt-1.5">
            <Note tone="info">
              An assignment that was made and pulled is kept, not deleted. Who was asked and
              when is part of the record.
            </Note>
          </div>
        </DrawerSection>
      )}

      {adding && (
        <AssignDialog
          eventId={event.id}
          onClose={() => setAdding(false)}
          onDone={() => { setAdding(false); setTick((t) => t + 1); onChanged() }}
        />
      )}
    </div>
  )
}

function AssignDialog({
  eventId, onClose, onDone,
}: { eventId: string; onClose: () => void; onDone: () => void }) {
  const [q, setQ] = useState('')
  const [userId, setUserId] = useState('')
  const [roleCode, setRoleCode] = useState<string>('CAMERA')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounced, like every other search in this app. Without it this fires a
  // request per keystroke against the user directory.
  const debounced = useDebounced(q, 300)
  const { data, loading } = useLoad(
    () => listUsers({ ...EMPTY_USER_FILTERS, q: debounced }, 0, 25), [debounced],
  )
  const users = data?.content ?? []

  return (
    <Modal open onClose={onClose} title="Assign somebody to film this">
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Note tone="info">
          Assigning does not grant permission to broadcast — that is a role decision. It
          books the camera slot and tells them it is theirs.
        </Note>

        <label className="block space-y-1">
          <span className="text-erp-xs font-medium">Person</span>
          <input className="erp-input w-full" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email" aria-label="Search people" />
        </label>

        {/* An empty six-row box means two different things. Say which. */}
        {loading && users.length === 0 ? (
          <p className="text-erp-xs text-muted-foreground">Searching…</p>
        ) : users.length === 0 ? (
          <p className="text-erp-xs text-muted-foreground">
            {debounced ? 'Nobody matched.' : 'Type a name or an email.'}
          </p>
        ) : (
          <select className="erp-input w-full" size={6} value={userId} aria-label="Person"
            onChange={(e) => setUserId(e.target.value)}>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
            ))}
          </select>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-erp-xs font-medium">Role</span>
            <select className="erp-input w-full" value={roleCode}
              onChange={(e) => setRoleCode(e.target.value)}>
              {ASSIGNMENT_ROLE_ORDER.map((r) => (
                <option key={r} value={r}>{assignmentRoleLabel(r)}</option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-erp-xs font-medium">Note</span>
            <input className="erp-input w-full" value={note}
              onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!userId || busy}
            onClick={async () => {
              setBusy(true); setError(null)
              try {
                await createAssignment(eventId, { userId, roleCode, note: note.trim() || null })
                onDone()
              } catch (e) { setError(explain(e)) } finally { setBusy(false) }
            }}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Assign
          </button>
        </div>
      </div>
    </Modal>
  )
}
